import { ChangeEvent, FC } from "react";
import { Box, IconButton, Stack, SxProps, TextField } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconDelete } from "@/assets/icons";
import { black, divider } from "@/theme/colors";
import { TCaseMatchTagColorKey } from "@/features/case/constants/case-match-tag-colors";
import { CaseMatchTagColorPicker } from "@/features/case/components/case-match-tag-editor/CaseMatchTagColorPicker";
import { TEditorRow } from "@/features/case/components/case-match-tag-editor/case-match-tag-editor-types";

type TCaseMatchTagEditorRowProps = {
  row: TEditorRow;
  usedColors: string[];
  maxLabelLength: number;
  onChangeLabel: (key: string, label: string) => void;
  onChangeColor: (key: string, color: TCaseMatchTagColorKey) => void;
  onDelete: (key: string) => void;
};

const ROW_SX: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  p: 1,
  border: `1px solid ${divider.middle}`,
  borderRadius: "8px",
  backgroundColor: "#fff",
};
const DRAG_HANDLE_SX: SxProps = { display: "flex", cursor: "grab", color: black[60] };
const DRAG_ICON_SX: SxProps = { fontSize: 20 };
const TEXTFIELD_SX: SxProps = { "& .MuiOutlinedInput-root": { borderRadius: "8px" } };

export const CaseMatchTagEditorRow: FC<TCaseMatchTagEditorRowProps> = ({
  row,
  usedColors,
  maxLabelLength,
  onChangeLabel,
  onChangeColor,
  onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.key });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const handleLabelChange = (e: ChangeEvent<HTMLInputElement>) => onChangeLabel(row.key, e.target.value);
  const handleColorChange = (color: TCaseMatchTagColorKey) => onChangeColor(row.key, color);
  const handleDelete = () => onDelete(row.key);

  return (
    <Box ref={setNodeRef} style={dragStyle} sx={ROW_SX}>
      <Box {...attributes} {...listeners} sx={DRAG_HANDLE_SX}>
        <DragIndicatorIcon sx={DRAG_ICON_SX} />
      </Box>
      <CaseMatchTagColorPicker value={row.color} disabledKeys={usedColors} onChange={handleColorChange} />
      <TextField
        value={row.label}
        onChange={handleLabelChange}
        placeholder="ラベルを入力"
        size="small"
        fullWidth
        inputProps={{ maxLength: maxLabelLength }}
        sx={TEXTFIELD_SX}
      />
      <Stack direction="row" alignItems="center">
        <IconButton onClick={handleDelete} size="small">
          <IconDelete width={20} height={20} color={black[60]} />
        </IconButton>
      </Stack>
    </Box>
  );
};
